import { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  ReferenceLine,
} from "recharts";

const monthsOrder = ["Feb", "Apr", "Jun", "Aug", "Oct", "Dec"];

function CustomTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    const { ageGroup, rank } = payload[0].payload;
    return (
      <div className="bg-white border border-gray-300 p-2 rounded shadow text-sm">
        <p>{`${ageGroup}: ${rank}`}</p>
      </div>
    );
  }
  return null;
}

export default function SinglePlayerRankingProgression({ ajssRankings }) {
  const { chartData, playerName, transitions } = useMemo(() => {
    if (!ajssRankings?.length)
      return { chartData: [], playerName: "", transitions: [] };

    const sorted = [...ajssRankings].sort((a, b) => {
      const yearDiff = a.year - b.year;
      if (yearDiff !== 0) return yearDiff;
      return monthsOrder.indexOf(a.month) - monthsOrder.indexOf(b.month);
    });

    const playerName = sorted[0]?.playerName || "";

    const chartData = sorted.map((r) => ({
      date: `${r.month} ${r.year}`,
      rank: r.rank,
      ageGroup: r.ageGroup,
    }));

    // detect transitions (where age group changes)
    const transitions = [];
    let currentGroup = sorted[0]?.ageGroup;
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].ageGroup !== currentGroup) {
        transitions.push({
          date: `${sorted[i].month} ${sorted[i].year}`,
          ageGroup: sorted[i].ageGroup,
        });
        currentGroup = sorted[i].ageGroup;
      }
    }

    return { chartData, playerName, transitions };
  }, [ajssRankings]);

  return (
    <div className="p-4 bg-white shadow rounded-xl">
      <h1 className="mb-4">Asian Junior Ranking 2025</h1>

      {chartData.length ? (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ right: 50, top: 40 }}>
            <XAxis dataKey="date" />
            <YAxis
              reversed
              allowDecimals={false}
              label={{ value: "Ranking", angle: -90, position: "insideLeft" }}
            />
            <Tooltip content={<CustomTooltip />} />

            <Line
              type="monotone"
              dataKey="rank"
              stroke="#1f77b4"
              strokeWidth={2}
              dot={{ r: 3 }}
              name={playerName}
            >
              <LabelList dataKey="rank" position="top" offset={6} fontSize={12} />
            </Line>

            {/* Dotted lines + text labels for age group changes */}
            {transitions.map((t, i) => (
              <ReferenceLine
                key={i}
                x={t.date}
                stroke="red"
                strokeDasharray="4 4"
                label={{
                  value: t.ageGroup,
                  position: "top",
                  fontSize: 13,
                  fill: "red",
                  fontWeight: "bold",
                }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <p className="text-gray-500">No ranking data available</p>
      )}
    </div>
  );
}
